import {
	booleanAttribute,
	Component,
	computed,
	Input,
	OnInit,
	QueryList,
	ViewChildren,
	ChangeDetectionStrategy,
} from "@angular/core"
import {
	PolicyFormVersionField,
	PolicyFormVersionFieldOption,
} from "@app/definitions/types"
import { stripTags } from "@app/helpers"
import { Modalable } from "@app/Modalable"
import { NgTemplateOutlet } from "@angular/common"
import { BtnExtComponent, SpinOrContent } from "@app/utils/utils.component"
import { WysiwygComponent } from "@app/utils/wysiwyg/wysiwyg.component"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { GenSelectComponent } from "@app/gen-inputs/gen-select/gen-select.component"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { ConfirmComponent } from "@app/confirm/confirm.component"
import { COMMON_IMPORTS } from "../../../common-imports"

@Component({
	selector: "fields",
	imports: [
		COMMON_IMPORTS,
		NgTemplateOutlet,
		SpinOrContent,
		WysiwygComponent,
		LangValPipe,
		GenSelectComponent,
		GenButtonGroupComponent,
		GenInputComponent,
		BtnExtComponent,
		ModalWrapComponent,
		ConfirmComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./fields.component.html",
})
export class Fields extends Modalable {
	modalProps: any = null
	@Input() objectRow: any = null
	@Input({ transform: booleanAttribute }) disabled: boolean = false
	newItem: PolicyFormVersionField = new PolicyFormVersionField()
	items: PolicyFormVersionField[] = []
	@ViewChildren("optValue") optValues: QueryList<any> //all fields that can receive focus, used to focus on them
	isNewSaving: boolean = false
	isNewOpen: boolean = false

	async ngOnInit() {
		const res: any = await this.store.get_gen_items(
			"policy_form_version_fields"
		)
		if (res?.policy_form_version_fields) {
			this.items = res.policy_form_version_fields.filter(
				(row) => row.version_id == this.objectRow.id
			)
			this.items.forEach((row) => (row.errors = {}))
			this.prepRows()
		}
		if (this.disabled) {
			return
		}
		this.createNewItem()
	}

	prepRows() {
		this.items.sort(
			(a: PolicyFormVersionField, b: PolicyFormVersionField) =>
				a.order_num - b.order_num
		)
		this.items.forEach((row) => {
			row.optionsArr = []
			if (row.options) {
				row.optionsArr = JSON.parse(row.options)
				row.optionsArr.forEach((opt, pos) => (opt.order_num = pos + 1))
			}
			this.sortRowOptionsArr(row)
			row.is_highest_order = false
			row.is_lowest_order = false
		})
		if (this.items.length) {
			this.items[0].is_lowest_order = true
			this.items.at(-1)!.is_highest_order = true
		}
	}

	async moveOrderNum(row: PolicyFormVersionField, toLowerOrderNum: boolean) {
		const rowToFlip = this.store.findClosestOrderNum(
			this.items,
			row.order_num,
			toLowerOrderNum
		)
		if (rowToFlip) {
			const flipOrderNum = rowToFlip.order_num
			row.isFlipOrderSending = true
			const res = await this.apiService.post("flip_order_num", {
				id1: row.id,
				id2: rowToFlip.id,
				tableName: "policy_form_version_fields",
			})
			row.isFlipOrderSending = false
			if (res.success) {
				this.modalService.openToast("update_successfuly")
				rowToFlip.order_num = row.order_num
				row.order_num = flipOrderNum
				this.store.updGenItemRowField(
					"policy_form_version_fields",
					row.id,
					"order_num",
					row.order_num
				)
				this.store.updGenItemRowField(
					"policy_form_version_fields",
					rowToFlip.id,
					"order_num",
					rowToFlip.order_num
				)
				this.prepRows()
			} else {
				this.modalService.openToast("save_failed")
			}
		}
	}

	updateRowFieldsPreSave(row: PolicyFormVersionField) {
		if (row.field_type === "text") {
			row.optionsArr = []
			row.options = ""
		} else {
			//options
			row.options = JSON.stringify(
				row.optionsArr.map((it) => ({ value: it.value }))
			)
		}
	}
	validateEntireForm(row: PolicyFormVersionField, isKeyup: boolean = false) {
		//validate all fields and detrmine if can be submitted (or has errors)
		this.updateRowFieldsPreSave(row)
		row.errors = {}

		const validationRules: any = {
			question: { not_empty: true },
		}
		if (row.field_type == "remark") {
			row.required = "no"
		}

		row.isFormSubmittable = this.validator.validateEntireForm(
			row,
			row.errors,
			validationRules,
			this.items
		)

		if (!["text", "remark"].includes(row.field_type)) {
			//options
			if (row.optionsArr.length < 2) {
				row.isFormSubmittable = false
				row.errors.options = this.lang.getVal("at_least_2")
			} else {
				for (let opt of row.optionsArr) {
					if (!opt.value.trim()) {
						row.isFormSubmittable = false
						row.errors.options = this.validator.replaceFieldName(
							"options",
							this.lang.getVal("is_empty")
						)
						break
					}
				}
			}
		}

		if (row.isFormSubmittable && row.id && !isKeyup) {
			this.updateItem(row)
		}
	}
	async updateItem(row: PolicyFormVersionField) {
		const res = await this.apiService.post(
			"update_policy_form_version_field",
			row
		)
		this.modalService.openToast("update_successfuly")
		this.store.siteDataUpdateRow("policy_form_version_fields", row)
	}

	async saveNewItem() {
		if (!this.newItem.isFormSubmittable) {
			return
		}
		const sendObj = { ...this.newItem }
		delete sendObj.errors
		this.isNewSaving = true
		const res: any = await this.apiService.save_gen_item(
			"policy_form_version_fields",
			sendObj
		)
		this.isNewSaving = false
		if (res?.id) {
			res.errors = {}
			this.store.addGenItemRow("policy_form_version_fields", res)
			this.items = [...this.items, res]
			this.prepRows()
			this.createNewItem()
			this.modalService.openToast("saved_successfully")
		} else {
			this.modalService.openToast("save_failed")
		}
	}

	createNewItem() {
		this.newItem = new PolicyFormVersionField()
		this.newItem.version_id = this.objectRow.id

		if (this.items.length) {
			const lastItem = this.items.at(-1)
			this.newItem.field_type = lastItem!.field_type
			this.newItem.optionsArr = lastItem?.optionsArr?.length
				? [...lastItem.optionsArr.map((it) => ({ ...it }))]
				: []
		}
	}

	deleteItem(row: PolicyFormVersionField) {
		this.modalProps = {
			name: "confirm-delete",
			row,
			objectName: stripTags(row.question),
		}
	}
	async handleDeleteApprove(approved: boolean) {
		const { row } = this.modalProps
		this.modalProps = null
		if (!approved) {
			return
		}

		row.isDeleting = true
		const res: any = await this.apiService.delete_gen_item(
			"policy_form_version_fields",
			row.id
		)
		row.isDeleting = false
		if (res.success) {
			this.modalService.openToast("delete_successful")
			this.store.delGenItemRow("policy_form_version_fields", row.id)
			this.items = this.items.filter((it) => it.id != row.id)
			this.prepRows()
		} else {
			this.modalService.openToast("delete_failed")
		}
	}

	sortRowOptionsArr(row: PolicyFormVersionField) {
		row.optionsArr.sort(
			(a: PolicyFormVersionFieldOption, b: PolicyFormVersionFieldOption) =>
				a.order_num - b.order_num
		)
		row.optionsArr.forEach((opt) => (opt.is_highest_order = false))
		if (row.optionsArr?.length) {
			row.optionsArr.at(-1)!.is_highest_order = true
		}
	}

	removeOpt(row: PolicyFormVersionField, opt: PolicyFormVersionFieldOption) {
		row.optionsArr = row.optionsArr.filter(
			(optItem) => optItem.order_num != opt.order_num
		)
		let pos = 1
		for (let opt of row.optionsArr) {
			opt.order_num = pos
			pos++
		}
		this.sortRowOptionsArr(row)
		this.validateEntireForm(row)
	}
	async addOpt(row: PolicyFormVersionField) {
		const order_num = this.getOptionsArrTopOrderNum(row) + 1
		row.optionsArr = [...row.optionsArr, { order_num, value: "" }]
		this.sortRowOptionsArr(row)
		this.validateEntireForm(row)

		const dataRowField = (row?.id || 0) + "_" + order_num
		if (!this.optValues) {
			return
		}
		await this.store.timeout(100)
		this.optValues
			.find((component) => component.dataRowField == dataRowField)
			?.focus()
	}
	getOptionsArrTopOrderNum(row: PolicyFormVersionField) {
		return row.optionsArr.length
			? Math.max(...row.optionsArr.map(({ order_num }) => order_num))
			: 0
	}
	moveOptOrderNum(
		row: PolicyFormVersionField,
		opt: PolicyFormVersionFieldOption,
		toLowerOrderNum: boolean
	) {
		const orderNumToSearch = opt.order_num + (toLowerOrderNum ? -1 : 1)
		const optToFlip = row.optionsArr.find(
			(t) => t.order_num == orderNumToSearch
		)

		if (optToFlip) {
			optToFlip.order_num = opt.order_num
			opt.order_num = orderNumToSearch
		}
		this.sortRowOptionsArr(row)
		this.validateEntireForm(row)
	}
}
